import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  ArrowRight, 
  ArrowLeft, 
  Search, 
  Building2, 
  DollarSign, 
  Calendar, 
  HeartHandshake, 
  Dog, 
  Sparkles,
  MapPin,
  ShieldCheck,
  Check
} from "lucide-react";

const LONDON_BOROUGHS = [
  "Kensington and Chelsea",
  "Westminster",
  "Islington",
  "Camden",
  "Canary Wharf",
  "Clapham",
  "Marylebone",
  "Mayfair",
  "Richmond upon Thames",
  "Hackney",
  "Southwark",
  "Hammersmith and Fulham",
  "Wandsworth",
  "Greenwich"
];

const PROPERTY_TYPES = [
  "Flat / Apartment",
  "Penthouse",
  "Studio",
  "House",
  "Mansion Flat",
  "Mews House"
];

export const FindHome = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submittedBrief, setSubmittedBrief] = useState(null);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    target_boroughs: ["Kensington and Chelsea", "Islington"],
    property_types: ["Flat / Apartment"],
    min_bedrooms: 1,
    max_bedrooms: 2,
    budget_pcm_min: 2500,
    budget_pcm_max: 3500,
    move_in_date: "2026-07-01",
    tenancy_length_months: 12,
    household_composition: "Couple",
    pets: false,
    pet_details: "",
    outside_space: true,
    parking: false,
    furnishing_preference: "Furnished",
    commute_destinations: "Green Park / City of London",
    additional_notes: ""
  });

  const toggleBorough = (b) => {
    setFormData((prev) => {
      const exists = prev.target_boroughs.includes(b);
      if (exists) {
        if (prev.target_boroughs.length === 1) return prev;
        return { ...prev, target_boroughs: prev.target_boroughs.filter((item) => item !== b) };
      } else {
        return { ...prev, target_boroughs: [...prev.target_boroughs, b] };
      }
    });
  };

  const togglePropType = (t) => {
    setFormData((prev) => {
      const exists = prev.property_types.includes(t);
      if (exists) {
        if (prev.property_types.length === 1) return prev;
        return { ...prev, property_types: prev.property_types.filter((item) => item !== t) };
      } else {
        return { ...prev, property_types: [...prev.property_types, t] };
      }
    });
  };

  const handleNext = () => {
    if (step === 1 && formData.target_boroughs.length === 0) {
      toast.error("Please select at least one London borough.");
      return;
    }
    if (step === 3 && Number(formData.budget_pcm_min) > Number(formData.budget_pcm_max)) {
      toast.error("Minimum budget cannot exceed maximum budget.");
      return;
    }
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.email) {
      toast.error("Please enter your name and email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/briefs", formData);
      setSubmittedBrief(res.data);
      toast.success("Renter brief submitted to FIND LET Concierge Desk!");
      setStep(6);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit brief. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFF] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        
        {/* Header Title */}
        <div className="text-center space-y-2 mb-8">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-[#D0E4FE] text-blue-800 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            <span>5-Step Renter Intake Wizard</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#091F5C] font-heading">
            Define Your London Home Brief
          </h1>
          <p className="text-[#4A5B78] text-sm max-w-lg mx-auto">
            Specify your requirements once. Our concierge desk curates off-market & verified inventory directly to your portal.
          </p>
        </div>

        {/* Progress Bar */}
        {step <= 5 && (
          <div className="mb-8">
            <div className="flex items-center justify-between text-xs font-medium text-[#4A5B78] mb-2">
              <span>Step {step} of 5</span>
              <span>
                {step === 1 && "Target Boroughs & Commute"}
                {step === 2 && "Property Type & Bedrooms"}
                {step === 3 && "Budget & Move Date"}
                {step === 4 && "Household & Non-Negotiables"}
                {step === 5 && "Review & Submit"}
              </span>
            </div>
            <div className="h-2 w-full bg-[#D0E4FE] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#091F5C] transition-all duration-300 rounded-full"
                style={{ width: `${(step / 5) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Card Container */}
        <div className="bg-white rounded-2xl p-6 sm:p-10 border border-[#E5EBF5] shadow-xl">
          
          {/* STEP 1: Boroughs & Commute */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[#091F5C] font-heading">
                  Where in London do you want to live?
                </h2>
                <p className="text-xs text-[#4A5B78] mt-1">
                  Select one or multiple target boroughs. You can also specify tube stations or commute limits.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {LONDON_BOROUGHS.map((b) => {
                  const isSelected = formData.target_boroughs.includes(b);
                  return (
                    <button
                      key={b}
                      type="button"
                      onClick={() => toggleBorough(b)}
                      data-testid={`borough-btn-${b.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`p-3 rounded-xl border text-left text-xs font-medium transition-all flex items-center justify-between ${
                        isSelected 
                          ? "bg-[#E8F2FE] border-[#D0E4FE] text-blue-900 font-semibold shadow-sm"
                          : "border-[#E5EBF5] hover:border-[#D0E4FE] text-[#4A5B78]"
                      }`}
                    >
                      <span>{b}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-[#334DAF] flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>

              <div className="pt-2">
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                  Key Commute Target or Tube Lines (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Under 30 mins to Bank, Elizabeth Line or Victoria Line"
                  value={formData.commute_destinations}
                  onChange={(e) => setFormData({ ...formData, commute_destinations: e.target.value })}
                  data-testid="input-commute-destinations"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                />
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleNext}
                  data-testid="wizard-step1-next"
                  className="px-6 py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-sm flex items-center space-x-2"
                >
                  <span>Continue to Property Specs</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Property Type & Bedrooms */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[#091F5C] font-heading">
                  Property Type & Bedroom Requirements
                </h2>
                <p className="text-xs text-[#4A5B78] mt-1">
                  Choose the structural configuration that matches your lifestyle.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                  Property Types (Select all that apply)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {PROPERTY_TYPES.map((t) => {
                    const isSelected = formData.property_types.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => togglePropType(t)}
                        data-testid={`prop-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`p-3 rounded-xl border text-left text-xs font-medium transition-all flex items-center justify-between ${
                          isSelected 
                            ? "bg-[#E8F2FE] border-[#D0E4FE] text-blue-900 font-semibold"
                            : "border-[#E5EBF5] hover:border-[#D0E4FE] text-[#4A5B78]"
                        }`}
                      >
                        <span>{t}</span>
                        {isSelected && <Check className="w-3.5 h-3.5 text-[#334DAF] flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Min Bedrooms
                  </label>
                  <select
                    value={formData.min_bedrooms}
                    onChange={(e) => setFormData({ ...formData, min_bedrooms: Number(e.target.value) })}
                    data-testid="select-min-bedrooms"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  >
                    <option value={1}>1 Bedroom</option>
                    <option value={2}>2 Bedrooms</option>
                    <option value={3}>3 Bedrooms</option>
                    <option value={4}>4+ Bedrooms</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Max Bedrooms
                  </label>
                  <select
                    value={formData.max_bedrooms}
                    onChange={(e) => setFormData({ ...formData, max_bedrooms: Number(e.target.value) })}
                    data-testid="select-max-bedrooms"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  >
                    <option value={1}>1 Bedroom</option>
                    <option value={2}>2 Bedrooms</option>
                    <option value={3}>3 Bedrooms</option>
                    <option value={4}>4+ Bedrooms</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 flex justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-5 py-2.5 rounded-xl border border-[#D0E4FE] text-[#4A5B78] font-medium text-sm flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  data-testid="wizard-step2-next"
                  className="px-6 py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-sm flex items-center space-x-2"
                >
                  <span>Continue to Budget & Dates</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Budget & Move Date */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[#091F5C] font-heading">
                  Monthly Budget & Move-In Timeline
                </h2>
                <p className="text-xs text-[#4A5B78] mt-1">
                  We verify pricing transparency and match within your stated range.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Min Budget (£ PCM)
                  </label>
                  <input
                    type="number"
                    step="50"
                    value={formData.budget_pcm_min}
                    onChange={(e) => setFormData({ ...formData, budget_pcm_min: Number(e.target.value) })}
                    data-testid="input-budget-min"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF] font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Max Budget (£ PCM)
                  </label>
                  <input
                    type="number"
                    step="50"
                    value={formData.budget_pcm_max}
                    onChange={(e) => setFormData({ ...formData, budget_pcm_max: Number(e.target.value) })}
                    data-testid="input-budget-max"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF] font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Target Move-In Date
                  </label>
                  <input
                    type="date"
                    value={formData.move_in_date}
                    onChange={(e) => setFormData({ ...formData, move_in_date: e.target.value })}
                    data-testid="input-move-date"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Tenancy Length
                  </label>
                  <select
                    value={formData.tenancy_length_months}
                    onChange={(e) => setFormData({ ...formData, tenancy_length_months: Number(e.target.value) })}
                    data-testid="select-tenancy-length"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  >
                    <option value={6}>6 Months (Short Stay)</option>
                    <option value={12}>12 Months (Standard)</option>
                    <option value={24}>24 Months (Long Term)</option>
                    <option value={36}>36 Months</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 flex justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-5 py-2.5 rounded-xl border border-[#D0E4FE] text-[#4A5B78] font-medium text-sm flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  data-testid="wizard-step3-next"
                  className="px-6 py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-sm flex items-center space-x-2"
                >
                  <span>Continue to Household Specs</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Household & Non-Negotiables */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[#091F5C] font-heading">
                  Household Composition & Non-Negotiables
                </h2>
                <p className="text-xs text-[#4A5B78] mt-1">
                  Ensure concierge matches respect your lifestyle requirements.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Household Composition
                  </label>
                  <select
                    value={formData.household_composition}
                    onChange={(e) => setFormData({ ...formData, household_composition: e.target.value })}
                    data-testid="select-household-composition"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  >
                    <option value="Single Professional">Single Professional</option>
                    <option value="Couple">Couple</option>
                    <option value="Sharers">Sharers / Co-living</option>
                    <option value="Family">Family</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Furnishing Preference
                  </label>
                  <select
                    value={formData.furnishing_preference}
                    onChange={(e) => setFormData({ ...formData, furnishing_preference: e.target.value })}
                    data-testid="select-furnishing"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  >
                    <option value="Furnished">Furnished (Turnkey)</option>
                    <option value="Unfurnished">Unfurnished</option>
                    <option value="Flexible">Flexible / Open to negotiation</option>
                  </select>
                </div>
              </div>

              {/* Lifestyle Toggles */}
              <div className="space-y-3 pt-2">
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider">
                  Lifestyle Non-Negotiables
                </label>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className={`p-4 rounded-xl border cursor-pointer flex items-center space-x-3 transition-colors ${
                    formData.pets ? "bg-[#E8F2FE] border-[#D0E4FE] text-blue-950 font-semibold" : "border-[#E5EBF5] hover:border-[#D0E4FE]"
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.pets}
                      onChange={(e) => setFormData({ ...formData, pets: e.target.checked })}
                      data-testid="checkbox-pets"
                      className="w-4 h-4 text-[#334DAF] rounded"
                    />
                    <div className="text-xs">
                      <div className="font-semibold">Pet-Friendly Required</div>
                      <div className="text-[#4A5B78] text-[10px]">Dogs/Cats</div>
                    </div>
                  </label>

                  <label className={`p-4 rounded-xl border cursor-pointer flex items-center space-x-3 transition-colors ${
                    formData.outside_space ? "bg-[#E8F2FE] border-[#D0E4FE] text-blue-950 font-semibold" : "border-[#E5EBF5] hover:border-[#D0E4FE]"
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.outside_space}
                      onChange={(e) => setFormData({ ...formData, outside_space: e.target.checked })}
                      data-testid="checkbox-outside-space"
                      className="w-4 h-4 text-[#334DAF] rounded"
                    />
                    <div className="text-xs">
                      <div className="font-semibold">Balcony / Garden</div>
                      <div className="text-[#4A5B78] text-[10px]">Private outdoor space</div>
                    </div>
                  </label>

                  <label className={`p-4 rounded-xl border cursor-pointer flex items-center space-x-3 transition-colors ${
                    formData.parking ? "bg-[#E8F2FE] border-[#D0E4FE] text-blue-950 font-semibold" : "border-[#E5EBF5] hover:border-[#D0E4FE]"
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.parking}
                      onChange={(e) => setFormData({ ...formData, parking: e.target.checked })}
                      data-testid="checkbox-parking"
                      className="w-4 h-4 text-[#334DAF] rounded"
                    />
                    <div className="text-xs">
                      <div className="font-semibold">Parking Space</div>
                      <div className="text-[#4A5B78] text-[10px]">Allocated/Permit</div>
                    </div>
                  </label>
                </div>
              </div>

              {formData.pets && (
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Pet Details (Breed, Age, Training)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 1 French Bulldog, 2 years old, crate trained & quiet"
                    value={formData.pet_details}
                    onChange={(e) => setFormData({ ...formData, pet_details: e.target.value })}
                    data-testid="input-pet-details"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  />
                </div>
              )}

              <div className="pt-4 flex justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-5 py-2.5 rounded-xl border border-[#D0E4FE] text-[#4A5B78] font-medium text-sm flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  data-testid="wizard-step4-next"
                  className="px-6 py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-sm flex items-center space-x-2"
                >
                  <span>Review Brief</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: Review & Submit */}
          {step === 5 && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[#091F5C] font-heading">
                  Review & Assign to Concierge Desk
                </h2>
                <p className="text-xs text-[#4A5B78] mt-1">
                  Enter your contact details. We will link this brief to your tenant portal.
                </p>
              </div>

              {/* Brief Summary Box */}
              <div className="p-4 rounded-xl bg-[#F9FBFF] border border-[#E5EBF5] text-xs space-y-2 text-[#4A5B78]">
                <div className="flex justify-between">
                  <span className="font-semibold text-[#091F5C]">Boroughs:</span>
                  <span className="text-[#334DAF] font-medium">{formData.target_boroughs.join(", ")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-[#091F5C]">Type & Beds:</span>
                  <span>{formData.property_types.join(", ")} ({formData.min_bedrooms} - {formData.max_bedrooms} Beds)</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-[#091F5C]">Budget Range:</span>
                  <span className="font-bold text-[#091F5C]">£{formData.budget_pcm_min.toLocaleString()} - £{formData.budget_pcm_max.toLocaleString()} PCM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-[#091F5C]">Target Move-In:</span>
                  <span>{formData.move_in_date} ({formData.tenancy_length_months} Months)</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-[#091F5C]">Non-Negotiables:</span>
                  <span>
                    {formData.pets ? "Pets Approved, " : ""}
                    {formData.outside_space ? "Balcony/Garden, " : ""}
                    {formData.parking ? "Parking, " : ""}
                    {formData.furnishing_preference}
                  </span>
                </div>
              </div>

              {/* Contact Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Eleanor Vane"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    data-testid="input-tenant-name"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="e.g. eleanor@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="input-tenant-email"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="+44 7700 900000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="input-tenant-phone"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Additional Notes for Concierge
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Need dedicated desk space or high ceilings"
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                    data-testid="input-tenant-notes"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                  />
                </div>
              </div>

              <div className="pt-4 flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-5 py-2.5 rounded-xl border border-[#D0E4FE] text-[#4A5B78] font-medium text-sm flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-brief-btn"
                  className="px-8 py-3.5 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-bold text-sm flex items-center space-x-2 shadow-lg shadow-blue-600/30 transition-all disabled:opacity-50"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>{submitting ? "Submitting Brief..." : "Submit Brief to Concierge Desk"}</span>
                </button>
              </div>
            </form>
          )}

          {/* STEP 6: Confirmation & Next Action */}
          {step === 6 && submittedBrief && (
            <div className="text-center py-8 space-y-6">
              <div className="w-16 h-16 rounded-full bg-[#D0E4FE] text-[#334DAF] flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-[#091F5C] font-heading">
                  Brief Assigned to London Concierge Desk
                </h2>
                <p className="text-sm text-[#4A5B78] max-w-md mx-auto">
                  Reference: <span className="font-mono font-bold text-[#091F5C]">{submittedBrief.id?.slice(0, 8)}</span>
                </p>
                <p className="text-xs text-[#4A5B78] max-w-md mx-auto">
                  A confirmation email has been dispatched. Our team is actively matching your criteria against private landlord stock and agency feeds.
                </p>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row justify-center gap-3">
                <Link
                  to="/tenant/dashboard"
                  data-testid="view-tenant-dashboard-btn"
                  className="px-6 py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-sm shadow-md"
                >
                  Go to Tenant Dashboard
                </Link>
                <Link
                  to="/move"
                  data-testid="book-move-service-btn"
                  className="px-6 py-3 rounded-xl bg-[#091F5C] hover:bg-slate-700 text-white font-semibold text-sm"
                >
                  Book FIND LET Move Relocation →
                </Link>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};
